import { billAmountCents, lineAmount, newLineKey, parseNonNegative, taxCents, type LineItem } from '@/lib/line-items';

/**
 * A bill's lines on the shared Line Items Section: how a line is read from / saved to the server, how a bill starts from a purchase order (a
 * photograph of its lines, never the same rows), and the three ways of saying how much is being billed:
 *   - Full Amount: every line gets what is still pending on its purchase-order line;
 *   - Split proportionally: a total is spread over the pending balances of the lines;
 *   - Manual: the user types each Bill Amount.
 * All money is whole cents. Nothing here touches the purchase order: a bill line is its own row (bill_items), with the purchase-order line it
 * covers remembered only as a reference (`sourceLineId`), so what bills billed of each order line can be added up.
 *
 * A bill is a COST, whether or not it is paid: Bill Amount is never reduced by payments (those are Bill Payments, a separate flow).
 */

/** One line of GET /api/purchase-orders/:id/billing */
export interface BillingLine {
  id: string;
  order_number: number;
  item_name: string;
  quantity: number | string;
  unit_price: number | string | null;
  unit: string | null;
  description: string | null;
  cost_code_id: string | null;
  catalog_item_id: string | null;
  amount: number;
  billed: number;
  pending: number;
}

/** GET /api/purchase-orders/:id/billing */
export interface PurchaseOrderBilling {
  purchase_order: { id: string; subtotal: number; tax_pct: number; tax: number; other_amount: number; total: number };
  lines: BillingLine[];
  billed: { lines: number; tax: number; other: number; unallocated: number; total: number; bills: Array<{ id: string; bill_id: string; name: string | null; status: string; total_amount: number; lines_total: number; tax: number; other_amount: number; line_level: boolean }> };
  pending: { lines: number; tax: number; other: number };
  reconciliation: { order_total: number; billed_total: number; difference: number };
}

const cents = (value: number) => Math.round(value * 100);
const fromCents = (value: number) => (value / 100).toFixed(2);

/** A saved bill line (bill_items row) as a line of the section. */
export function billLineFromRow(row: Record<string, any>): LineItem {
  const quantity = String(Number(row.quantity ?? 1));
  const unitPrice = String(Number(row.unit_price ?? 0));
  const line: LineItem = {
    key: row.id,
    catalogItemId: row.catalog_item_id ?? null,
    name: row.description ?? '',
    costCodeId: row.cost_code_id ?? '',
    description: '',
    unit: row.unit ?? '',
    quantity,
    unitPrice,
    priceSource: null,
    vendorId: null,
    linkBaseline: null,
    unlinkedNotice: false,
    // the Bill Amount is what was saved in total_price (it can differ from quantity x price)
    billAmount: String(Number(row.total_price ?? 0)),
    sourceLineId: row.purchase_order_item_id ?? null,
  };
  return line;
}

/** The lines as the API takes them (`bill_item` bulk replace). The cost code travels by id; `total_price` is the Bill Amount. */
export function billLinePayload(lines: LineItem[]): Array<Record<string, unknown>> {
  return lines
    .filter((line) => line.name.trim() !== '' || billAmountCents(line) > 0 || parseNonNegative(line.unitPrice) || line.sourceLineId)
    .map((line, index) => ({
      description: line.name,
      quantity: parseNonNegative(line.quantity) ?? 0,
      unit_price: parseNonNegative(line.unitPrice) ?? 0,
      total_price: billAmountCents(line) / 100,
      order_number: index + 1,
      cost_code_id: line.costCodeId || null,
      purchase_order_item_id: line.sourceLineId,
      catalog_item_id: line.catalogItemId,
      unit: line.unit.trim() || null,
    }));
}

/** A blank bill line: its Bill Amount follows quantity x price until the user types one. */
export function emptyBillLine(costCodeId = ''): LineItem {
  return {
    key: newLineKey(), catalogItemId: null, name: '', costCodeId, description: '', unit: '', quantity: '1', unitPrice: '0', priceSource: null,
    vendorId: null, linkBaseline: null, unlinkedNotice: false, billAmount: '0.00', sourceLineId: null,
  };
}

/**
 * A new bill line from a purchase-order line: a COPY of what it says (item, cost code, unit, quantity, price, catalog item) with its own
 * key and its own Bill Amount (0 until the user says what is billed). `sourceLineId` is the only thing that points back.
 */
export function billLineFromPoLine(line: BillingLine, billAmount = 0): LineItem {
  return {
    key: newLineKey(),
    catalogItemId: line.catalog_item_id,
    name: line.item_name,
    costCodeId: line.cost_code_id ?? '',
    description: '',
    unit: line.unit ?? '',
    quantity: String(Number(line.quantity)),
    unitPrice: String(Number(line.unit_price ?? 0)),
    priceSource: null,
    vendorId: null,
    linkBaseline: null,
    unlinkedNotice: false,
    billAmount: fromCents(cents(billAmount)),
    sourceLineId: line.id,
  };
}

/**
 * Editing a line that is NOT a copy of an order line: its Bill Amount follows quantity x price (as the amount of a bill line always did).
 * A line that covers an order line keeps the Bill Amount the user chose -- quantity and price there are only the order's own figures.
 */
export function syncBillAmounts(previous: LineItem[], next: LineItem[]): LineItem[] {
  const before = new Map(previous.map((line) => [line.key, line]));
  return next.map((line) => {
    const old = before.get(line.key);
    if (!old || line.sourceLineId) return line;
    if (old.quantity === line.quantity && old.unitPrice === line.unitPrice) return line;
    return { ...line, billAmount: lineAmount(line).toFixed(2) };
  });
}

/** What is still to bill, across the linked purchase orders: per order line, and the tax and other charges not billed yet (whole cents). */
export interface BillingBase {
  /** pending cents by purchase-order line id */
  lines: Map<string, number>;
  /** tax (cents) still to bill */
  tax: number;
  /** other amount (cents) still to bill */
  other: number;
  /** the order's own tax rate (%), when there is one: preferred when it explains the pending tax exactly */
  rate: number | null;
}

export function billingBase(billing: Record<string, PurchaseOrderBilling>): BillingBase {
  const orders = Object.values(billing);
  return {
    lines: new Map(orders.flatMap((b) => b.lines.map((l) => [l.id, cents(l.pending)] as [string, number]))),
    tax: orders.reduce((sum, b) => sum + cents(b.pending.tax), 0),
    other: orders.reduce((sum, b) => sum + cents(b.pending.other), 0),
    rate: orders.length > 0 ? Number(orders[0].purchase_order.tax_pct) || 0 : null,
  };
}

/** What is still pending on each order line, by id, in dollars (the hint under each Bill Amount). */
export const pendingByLine = (billing: Record<string, PurchaseOrderBilling>): Map<string, number> =>
  new Map(Object.values(billing).flatMap((b) => b.lines.map((l) => [l.id, l.pending] as [string, number])));

/** The components of a bill that can still be billed: its lines that cover order lines, the tax and the other charges (cents). */
export function pendingComponents(lines: LineItem[], base: BillingBase): { lines: number; tax: number; other: number; total: number } {
  const covered = lines.filter((line) => line.sourceLineId && base.lines.has(line.sourceLineId));
  const sum = covered.reduce((acc, line) => acc + (base.lines.get(line.sourceLineId as string) ?? 0), 0);
  return { lines: sum, tax: base.tax, other: base.other, total: sum + base.tax + base.other };
}

/** What a bill is to be filled with: the Bill Amount of its lines, and the Tax % / Other Amount that go with them. */
export interface BilledShape { lines: LineItem[]; taxPct: string; otherAmount: string }

const percentText = (value: number): string => String(Math.round(value * 10000) / 10000);

/**
 * Turns "this much on the lines, this much tax, this much other" (cents) into what the Bill form holds: the lines, a Tax % and an Other Amount
 * whose result is EXACTLY those cents. Tax is a percentage of the lines, so the percentage is chosen to give the tax (the order's own rate when it
 * does, else tax / lines to 4 decimals) and Other Amount absorbs the cents that rounding the percentage could not express -- the total never moves.
 */
function shapeOf(lines: LineItem[], amounts: Map<string, number>, tax: number, other: number, rate: number | null): BilledShape {
  const covered = Array.from(amounts.values()).reduce((a, b) => a + b, 0);
  let pct = 0;
  let wantedTax = tax;
  let wantedOther = other;
  if (covered > 0 && tax > 0) {
    pct = rate !== null && taxCents(covered, rate) === tax ? rate : Math.round((tax / covered) * 100 * 10000) / 10000;
  } else if (tax > 0) {
    // tax with no lines under it cannot be a percentage: it travels with the other charges
    wantedOther += tax;
    wantedTax = 0;
  }
  const realTax = covered > 0 ? taxCents(covered, pct) : 0;
  wantedOther += wantedTax - realTax;
  const nextLines = lines.map((line) => (amounts.has(line.key) ? { ...line, billAmount: fromCents(amounts.get(line.key) as number) } : line));
  if (wantedOther < 0) {
    // the rounded tax overshoots by a cent or two and there is no other charge to take it from: the largest line gives it
    const biggest = Array.from(amounts.entries()).sort((a, b) => b[1] - a[1])[0];
    if (biggest) {
      const index = nextLines.findIndex((line) => line.key === biggest[0]);
      nextLines[index] = { ...nextLines[index], billAmount: fromCents(biggest[1] + wantedOther) };
    }
    wantedOther = 0;
  }
  return { lines: nextLines, taxPct: pct > 0 ? percentText(pct) : '', otherAmount: wantedOther > 0 ? fromCents(wantedOther) : '' };
}

/**
 * Full Amount: everything still pending -- every line that covers an order line gets its pending amount (0 when fully billed), plus the tax and the
 * other charges not billed yet. Plain lines are left alone. Nothing is ever above what is pending.
 */
export function applyFullAmount(lines: LineItem[], base: BillingBase): BilledShape {
  const amounts = new Map(lines.filter((l) => l.sourceLineId && base.lines.has(l.sourceLineId)).map((l) => [l.key, base.lines.get(l.sourceLineId as string) as number]));
  return shapeOf(lines, amounts, base.tax, base.other, base.rate);
}

export type SplitResult = ({ ok: true } & BilledShape) | { ok: false; error: string };

/** Spreads `total` over `weights` (largest remainder, ties by position): the parts add up to the total exactly. */
function spread(total: number, weights: number[]): number[] {
  const sum = weights.reduce((a, b) => a + BigInt(b), BigInt(0));
  if (sum === BigInt(0) || total === 0) return weights.map(() => 0);
  const parts = weights.map((w, index) => ({ index, share: (BigInt(total) * BigInt(w)) / sum, rest: (BigInt(total) * BigInt(w)) % sum }));
  let left = BigInt(total) - parts.reduce((a, p) => a + p.share, BigInt(0));
  for (const part of [...parts].sort((a, b) => (a.rest === b.rest ? a.index - b.index : a.rest > b.rest ? -1 : 1))) {
    if (left <= BigInt(0)) break;
    if (part.rest > BigInt(0)) { part.share += BigInt(1); left -= BigInt(1); }
  }
  return parts.map((p) => Number(p.share));
}

/**
 * Split proportionally: `totalText` is the TOTAL to bill (lines + tax + other charges) and is spread over what is still pending of each of those
 * components in proportion, to the cent: the result adds up to exactly the amount asked. Lines fully billed get 0, and no component receives more than
 * it has pending. An amount above what can still be billed is refused (with the available amount) -- no wrong distribution is produced.
 */
export function splitProportionally(lines: LineItem[], base: BillingBase, totalText: string): SplitResult {
  const typed = parseNonNegative(totalText);
  if (typed === null || typed <= 0) return { ok: false, error: 'Enter the amount to bill (more than 0).' };
  const total = cents(typed);
  const eligible = lines.filter((l) => l.sourceLineId && (base.lines.get(l.sourceLineId) ?? 0) > 0);
  const balances = eligible.map((l) => base.lines.get(l.sourceLineId as string) as number);
  const linesPending = balances.reduce((a, b) => a + b, 0);
  const available = linesPending + base.tax + base.other;
  if (available === 0) return { ok: false, error: 'There is nothing left to bill on this purchase order.' };
  if (total > available) return { ok: false, error: `That is more than what is left to bill: ${(available / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}.` };
  const [forLines, forTax, forOther] = spread(total, [linesPending, base.tax, base.other]);
  const parts = spread(forLines, balances);
  const amounts = new Map<string, number>();
  lines.forEach((line) => { if (line.sourceLineId && base.lines.has(line.sourceLineId)) amounts.set(line.key, 0); });
  eligible.forEach((line, i) => amounts.set(line.key, parts[i]));
  return { ok: true, ...shapeOf(lines, amounts, forTax, forOther, base.rate) };
}

/** Lines whose Bill Amount is above what is pending on the order line they cover (shown and confirmed on save -- never silently cut). */
export function overBilledLines(lines: LineItem[], pending: Map<string, number>): LineItem[] {
  return lines.filter((line) => line.sourceLineId && pending.has(line.sourceLineId) && billAmountCents(line) > cents(pending.get(line.sourceLineId) ?? 0));
}
